import { useEffect, useRef, useState } from "react";
import { LayerData, LayerItem, SymbolicLayer } from "../../services/types";
import ConstructionProject from "./ConstructionProject";
import { formatValue } from "../../utils/helpers";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";
import { clearSymbolicLayers } from "../../utils/messages";

interface AnchorAreasProps {
    layerItems: LayerItem[]; 
    symbolicLayers: SymbolicLayer[] | null;
    activeLayerData: LayerData | undefined;
};

const AnchorAreas = ({ layerItems, symbolicLayers, activeLayerData }: AnchorAreasProps) => {
    const [selectedArea, setSelectedArea] = useState<string>("");
    const [selectedProject, setSelectedProject] = useState<LayerItem | null>(null);
    const [isSmallAreaActive, setIsSmallAreaActive] = useState<boolean>(false);
    // const [isProgramLayerActive, setIsProgramLayerActive] = useState<boolean>(false);

    const anchorRef = useRef<HTMLDivElement>(null);
    const origin = '*';

    const anchorAreas = [...new Set(layerItems.map(item => item.category).filter(category => category))];
    const orderedAnchors = anchorAreas.sort((a, b) => {
        if (a === 'Highland Heights') return -1;
        if (b === 'Highland Heights') return 1;
        return a.localeCompare(b);
    }); 

    const filteredProjects = selectedArea ? layerItems.filter(project => project.category === selectedArea) : layerItems;

    const totalInvestment = filteredProjects.reduce((total, project) => total + project.value, 0);

    useEffect(() => {
        if (selectedProject) {
            // Scroll to the bottom of the window when either a selected area or project is present
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [selectedProject]);

    const handleAreaClick = (area: string) => {
        if (selectedArea !== area) {
            setSelectedArea(area);
            const areaProjects = layerItems.filter(project => project.category === area);
    
            if (areaProjects.length > 0) {
                const firstProject = areaProjects[0];
                setSelectedProject(firstProject);
                showSmallAreaPlans(area);
                showProgramPoint(firstProject);
                console.log('selected area:', area, 'auto-selected project:', firstProject.name);
            } else {
                setSelectedProject(null); // Reset if there are no projects
                showSmallAreaPlans(area);
                console.log('selected area:', area, 'no projects to auto-select');
            }
        }
    };

    const handleProjectClick = (project: LayerItem) => {
        if (selectedProject?.external_id !== project.external_id) {
            setSelectedProject(project);
            showProgramPoint(project);
            console.log('selected project:', project?.name);
        }
    };

    const showSmallAreaPlans = (area: string) => {
        const smallAreaLayer = symbolicLayers?.find(layer => layer.label?.toLowerCase() === 'small area plans');

        // Send message to toggle small area plans symbolic layer
        if (smallAreaLayer && !isSmallAreaActive) {
            window.parent.postMessage(
                {
                    message: "toggleSymbolicLayer",
                    data: [{ label: smallAreaLayer.label, url: smallAreaLayer.url }],
                },
                origin
            )
            console.log('Toggle symbolic layer message sent:', [{ label: smallAreaLayer.label, url: smallAreaLayer.url }]);
            setIsSmallAreaActive(true);
        }

        console.log('area:', area);

        // if (area) {
        //     // Show current selected point item
        //     setTimeout(() => {
        //         window.parent.postMessage(
        //             {
        //                 message: "searchLayerItem",
        //                 data: {
        //                     layer_name: "Small Area Plans",
        //                     value: area
        //                 },
        //             },
        //             origin
        //         );
        //         console.log("Show small area plan message sent:", { layer_name: "Small Area Plans", value: area });
        //     }, 100);
        // }
    }

    const showProgramPoint = (project: LayerItem) => {
        // Only send "toggleProgrammaticLayer" if activeLayerData is defined
        if (activeLayerData) {
            const { layer_id, layer_name } = activeLayerData;
            window.parent.postMessage(
                {
                    message: "toggleProgrammaticLayer",
                    data: [{ id: layer_id, name: layer_name }],
                },
                origin
            );
            console.log("Toggle programmatic layer message sent:", [{ id: layer_id, name: layer_name }]);
        }

        if (project) {
            const { id, latitude, longitude } = project;
            // Show current selected point item
            setTimeout(() => {
                window.parent.postMessage(
                    {
                        message: "showProgram",
                        data: {
                            id,
                            latitude,
                            longitude,
                        },
                    },
                    origin
                );
                console.log("Program point message sent:", { id, latitude, longitude })
            }, 100);
        }
    };

    // Observer effect for showing selected radius around program point 
    useIntersectionObserver(anchorRef, (isVisible) => {
    const smallAreaLayer = symbolicLayers?.find(layer => layer.label?.toLowerCase() === 'small area plans');
        // Send message to toggle small area plans symbolic layer
        if (smallAreaLayer && isVisible) {
            // Clear current symbolic layer (planning districts)
            clearSymbolicLayers();

            window.parent.postMessage(
                {
                    message: "toggleSymbolicLayer",
                    data: [{ label: smallAreaLayer.label, url: smallAreaLayer.url }],
                },
                origin
            )
            console.log('Toggle symbolic layer message sent:', [{ label: smallAreaLayer.label, url: smallAreaLayer.url }]);
        }
    }, { threshold: 1, rootMargin: "0px 0px 0px 0px" });
    
    return (
        <div className={`${selectedArea && selectedProject ? 'mb-8' : selectedArea && !selectedProject  ? 'mb-[335px]' : 'mb-[465px]'}`}>
            {/* Totals Banner */}
            <div className="flex justify-center items-center space-x-16 bg-color-cai-blue rounded-md min-h-12 mb-8 mx-8">
                <p className="total-projects text-white"><strong>Total Projects:</strong> {filteredProjects.length}</p>
                <p className="text-white font-bold">Total Investment: {formatValue(totalInvestment, 'currency')}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mx-6" ref={anchorRef}>
                {orderedAnchors.map((area) => (
                    <div 
                        key={area} 
                        onClick={() => handleAreaClick(area)} 
                        className={`bg-white rounded-lg shadow-md hover:shadow-lg hover:cursor-pointer hover:bg-blue-50 hover:bg-opacity-50 transition-shadow p-4 border ${selectedArea === area ? 'border-blue-400' : 'border-gray-200'}`}
                    >
                        <h3 className="font-semibold text-center">
                            {area}
                        </h3>
                    </div>
                ))}
            </div>

            {/* Filtered Construction Projects Section */}
            {selectedArea && filteredProjects.length > 0 &&
            <> 
                {/* Construction Projects */}
                <h2 className='text-xl font-semibold text-center mt-8'>Construction Projects in {selectedArea}</h2>
                <div className="mt-6">
                    <ul className="flex items-center justify-center space-x-2">
                        {filteredProjects.map(project => (
                            <li 
                                key={project.external_id} 
                                onClick={() => handleProjectClick(project)} 
                                className={`p-3 border-2  rounded-md shadow-md hover:shadow-lg hover:cursor-pointer hover:bg-blue-50 hover:bg-opacity-50 transition-shadow text-memphis-blue ${selectedProject?.name === project.name ? 'border-blue-400' : 'border-memphis-blue'}`}
                            >
                                {project.name}
                            </li>
                        ))}
                    </ul>
                </div>
                {selectedProject && <ConstructionProject layerItem={selectedProject}/>}
            </>
            }
        </div>
    );
};

export default AnchorAreas;